import type {
	PricingEstimationService,
	PricingEstimationState,
	PricingEstimationTotal,
	PricingEstimationType,
	PricingEstimationTypeFixed,
	PricingEstimationTypeRange,
} from '../../types';

import { isMonthSeasonActive } from './season';
import { applyStartingCostFloor } from './starting-cost';
import { isFixedCost, widenFixedPricingEstimation } from './widen';
import {
	CENTS_SCALE,
	WIDEN_MAX_FACTOR,
	WIDEN_MIN_FACTOR,
} from './widening-constants';

const DEFAULT_CATEGORIES: PricingEstimationService[] = [
	'ceremony',
	'reception',
	'catering',
	'barServices',
];

const TOTAL_CATEGORIES = new Set<PricingEstimationService>([
	'ceremony',
	'reception',
]);

export const calculateTotalPricingEstimation = (
	pricingEstimation: PricingEstimationState | null,
	guestCount: number,
	selectedCategories?: PricingEstimationService[] | null,
	month?: number | null,
	startingCost?: number | null,
): PricingEstimationTotal | null => {
	if (!pricingEstimation) return null;

	const localEstimation = clonePricingEstimationState(pricingEstimation);
	const categoriesToCalculate = selectedCategories ?? DEFAULT_CATEGORIES;

	const shouldWiden = categoriesToCalculate.every(
		(category) => !blocksWidening(localEstimation[category]),
	);

	const totalCost: PricingEstimationTypeRange = {
		minCost: 0,
		maxCost: 0,
		unit: 'TOTAL',
	};

	// Pre-widening sum of the Fixed categories that will go through the
	// ±10% widening step. Used below to detect the "straddle" case where the
	// real estimate is above startingCost but the −10% reduction would dip
	// below it — in which case the warning copy would mislead the user.
	let realEstimate = 0;

	for (const category of categoriesToCalculate) {
		const categoryData = localEstimation[category];

		if (categoryData && !categoryData.unit) {
			categoryData.unit = TOTAL_CATEGORIES.has(category)
				? 'TOTAL'
				: 'PER_GUEST';
		}

		if (!totalCost.currency) {
			totalCost.currency = categoryData?.currency ?? 'USD';
		}

		if (shouldWiden) {
			realEstimate += accumulateRealEstimate(categoryData, guestCount);
		}

		const widened = shouldWiden
			? widenFixedPricingEstimation(categoryData)
			: categoryData;

		accumulateCategory(totalCost, widened, guestCount);
	}

	// Straddle case: the real estimate (no −10%) is at or above
	// startingCost, but the −10% widened min would dip below it. Widening
	// would cause the floor to bind and the warning to fire — but the
	// warning would compare the −10% value against startingCost, which
	// misrepresents what the couple's estimate actually is. Use the real
	// estimate as min and keep +10% on max so the displayed range still
	// reads as a range. The floor becomes a no-op so `isBelowMinimumSpend`
	// stays false and the warning copy is suppressed.
	//
	// Boundaries are inclusive on the upper side (`>=`) and strict on the
	// lower side (`<`):
	//   - realEstimate === startingCost: the couple's estimate exactly
	//     meets the floor, so a warning saying "your original estimate of
	//     $X is below $X" would be nonsensical. Treat as a straddle.
	//   - realEstimate * 0.9 === startingCost: the widened min exactly
	//     meets the floor and the floor is a no-op either way; including
	//     the equality here keeps the rule's behaviour continuous at the
	//     boundary.
	if (
		shouldWiden &&
		typeof startingCost === 'number' &&
		Number.isFinite(startingCost) &&
		realEstimate >= startingCost &&
		realEstimate * WIDEN_MIN_FACTOR < startingCost
	) {
		const widenedHigh =
			Math.round(realEstimate * WIDEN_MAX_FACTOR * CENTS_SCALE) / CENTS_SCALE;
		totalCost.minCost = realEstimate;
		totalCost.maxCost = Math.max(totalCost.maxCost, widenedHigh);
	}

	const naturalMinCost = totalCost.minCost;
	const naturalMaxCost = totalCost.maxCost;
	totalCost.minCost = applyStartingCostFloor(totalCost.minCost, startingCost);
	totalCost.maxCost = Math.max(totalCost.maxCost, totalCost.minCost);

	// When the floor dominates (the high bound is being held up by the floor
	// alone), widen the displayed number to [startingCost, max(high bound,
	// startingCost × 1.1)] so the PriceCard still reads as a range. Without
	// this, vendors where every selected FAQ category resolves to a degenerate
	// Range (e.g. Ravel Hotel — May is in both peakMonths and offPeakMonths)
	// collapse to a single price despite the displayed value being a venue-
	// wide floor.
	//
	// `originalMinCost` is what `MinimumSpendAlert` shows as "Your original
	// estimate of $X".
	//
	// Widen path (`shouldWiden` = true, every selected category resolved to a
	// Fixed shape): `naturalMinCost` is the −10% widened aggregate, which
	// under-reports the couple's actual estimate. Use the pre-widening
	// `realEstimate` instead so the warning copy matches what the calculator
	// would show without the −10% adjustment.
	//
	// No-widen path (`shouldWiden` = false, categories resolved to Ranges
	// via `buildRangeValue` because no specific date was picked): there is
	// no pre-widening single number — each category already spans a
	// peak/off-peak bracket. The lower end (`naturalMinCost` = sum of range
	// mins) is the optimistic best case, which would make the warning
	// under-state the estimate (e.g. "Your original estimate of $27,000 is
	// below this venue's minimum of $30,000" when the peak-side estimate
	// is $28,000). Use the upper end (`naturalMaxCost` = sum of range
	// maxes) — the peak-side aggregate — which is the most representative
	// "what the venue would charge" value when no date is selected.
	if (
		typeof startingCost === 'number' &&
		Number.isFinite(startingCost) &&
		totalCost.minCost === startingCost &&
		naturalMinCost < startingCost
	) {
		const widenedHigh =
			Math.round(startingCost * WIDEN_MAX_FACTOR * CENTS_SCALE) / CENTS_SCALE;
		totalCost.maxCost = Math.max(totalCost.maxCost, widenedHigh);
		totalCost.isBelowMinimumSpend = true;
		totalCost.originalMinCost = shouldWiden ? realEstimate : naturalMaxCost;
	}

	const isPeakSeason = isMonthSeasonActive(month, localEstimation.peakMonths);
	const isOffPeakSeason = isMonthSeasonActive(
		month,
		localEstimation.offPeakMonths,
	);

	return {
		ceremony: categoriesToCalculate.includes('ceremony')
			? localEstimation.ceremony
			: null,
		reception: categoriesToCalculate.includes('reception')
			? localEstimation.reception
			: null,
		catering: categoriesToCalculate.includes('catering')
			? localEstimation.catering
			: null,
		barServices: categoriesToCalculate.includes('barServices')
			? localEstimation.barServices
			: null,
		peakMonths: localEstimation.peakMonths,
		offPeakMonths: localEstimation.offPeakMonths,
		isPeakSeason: isPeakSeason ?? false,
		isOffPeakSeason: isOffPeakSeason ?? false,
		totalCost,
	};
};

const clonePricingEstimationState = (
	state: PricingEstimationState,
): PricingEstimationState => ({
	...state,
	ceremony: state.ceremony ? { ...state.ceremony } : null,
	reception: state.reception ? { ...state.reception } : null,
	catering: state.catering ? { ...state.catering } : null,
	barServices: state.barServices ? { ...state.barServices } : null,
});

const blocksWidening = (
	value: PricingEstimationType | null | undefined,
): boolean => {
	// A null/missing category is not a blocker — the question `shouldWiden`
	// asks is "do all the *non-null* categories resolve to Fixed shapes?",
	// and a missing category doesn't contribute to that check.
	if (value === null || value === undefined) return false;
	if (value.isFree) return false;
	return !isFixedCost(value);
};

const accumulateCategory = (
	totalCost: PricingEstimationTypeRange,
	categoryData: PricingEstimationType | null,
	guestCount: number,
): void => {
	if (!categoryData) return;

	if ('cost' in categoryData && categoryData.cost != null) {
		accumulateFixed(totalCost, categoryData, guestCount);
	} else if (
		'minCost' in categoryData &&
		'maxCost' in categoryData &&
		categoryData.cost == null
	) {
		accumulateRange(totalCost, categoryData, guestCount);
	}
};

// Returns the pre-widening contribution of a single category to the real
// (un-widened) estimate. Only counts categories that actually go through
// the ±10% widening step (same predicate as `widenFixedPricingEstimation`).
const accumulateRealEstimate = (
	categoryData: PricingEstimationType | null,
	guestCount: number,
): number => {
	if (!isFixedCost(categoryData)) return 0;
	return categoryData.unit === 'PER_GUEST'
		? categoryData.cost * guestCount
		: categoryData.cost;
};

const accumulateFixed = (
	totalCost: PricingEstimationTypeRange,
	categoryData: PricingEstimationTypeFixed,
	guestCount: number,
): void => {
	if (categoryData.unit === 'PER_GUEST') {
		totalCost.minCost += categoryData.cost * guestCount;
		totalCost.maxCost += categoryData.cost * guestCount;
	} else {
		totalCost.minCost += categoryData.cost;
		totalCost.maxCost += categoryData.cost;
	}
};

const accumulateRange = (
	totalCost: PricingEstimationTypeRange,
	categoryData: PricingEstimationTypeRange,
	guestCount: number,
): void => {
	const minCost = categoryData.minCost ?? 0;
	const maxCost = categoryData.maxCost ?? 0;
	if (categoryData.unit === 'PER_GUEST') {
		totalCost.minCost += minCost * guestCount;
		totalCost.maxCost += maxCost * guestCount;
	} else {
		totalCost.minCost += minCost;
		totalCost.maxCost += maxCost;
	}
};
